// All 83 system permission matrix IDs.
// Keys are safe TypeScript identifiers; values are the exact matrix_id strings stored in the DB.
export const PERMISSIONS = {
  ADMIN_FULL:                  'ADMIN_FULL',
  ASSIGN_ROLES:                'ASSIGN_ROLES',
  SCHEDULE_ADD_EDIT:           'SCHEDULE_ADD/EDIT',
  SCHEDULE_DELETE:             'SCHEDULE_DELETE',
  CLIENTS_ADD_EDIT:            'CLIENTS_ADD/EDIT',
  CLIENTS_DELETE:              'CLIENTS_DELETE',
  CLIENTS_SHOW_MENU:           'CLIENTS_SHOW_MENU',
  CLIENT_CREATE_REQUEST:       'CLIENT_CREATE_REQUEST',
  CLIENT_CREATE_QUOTE:         'CLIENT_CREATE_QUOTE',
  CLIENT_CREATE_JOB:           'CLIENT_CREATE_JOB',
  CLIENT_CREATE_INVOICE:       'CLIENT_CREATE_INVOICE',
  CLIENT_CREATE_AGREEMENT:     'CLIENT_CREATE_AGREEMENT',
  LEADS_ADD_EDIT:              'LEADS_ADD/EDIT',
  LEADS_DELETE:                'LEADS_DELETE',
  LEADS_SHOW_MENU:             'LEADS_SHOW_MENU',
  LEADS_CREATE_QUOTE:          'LEADS_CREATE_QUOTE',
  LEADS_PROMOTE_TO_CLIENT:     'LEADS_PROMOTE_TO_CLIENT',
  REQUESTS_ADD_EDIT:           'REQUESTS_ADD/EDIT',
  REQUESTS_DELETE:             'REQUESTS_DELETE',
  REQUESTS_SHOW_MENU:          'REQUESTS_SHOW_MENU',
  REQUESTS_CREATE_QUOTE:       'REQUESTS_CREATE_QUOTE',
  REQUESTS_CREATE_JOB:         'REQUESTS_CREATE_JOB',
  REQUESTS_CREATE_INVOICE:     'REQUESTS_CREATE_INVOICE',
  QUOTES_ADD_EDIT:             'QUOTES_ADD/EDIT',
  QUOTES_DELETE:               'QUOTES_DELETE',
  QUOTES_SHOW_MENU:            'QUOTES_SHOW_MENU',
  QUOTES_CREATE_JOB:           'QUOTES_CREATE_JOB',
  QUOTES_CREATE_INVOICE:       'QUOTES_CREATE_INVOICE',
  JOBS_ADD_EDIT:               'JOBS_ADD/EDIT',
  JOBS_DELETE:                 'JOBS_DELETE',
  JOBS_SHOW_MENU:              'JOBS_SHOW_MENU',
  JOB_CREATE_INVOICE:          'JOB_CREATE_INVOICE',
  JOB_SEND_PUNCHLIST:          'JOB_SEND_PUNCHLIST',
  JOB_SEND_REVIEW_REQUEST:     'JOB_SEND_REVIEW_REQUEST',
  INVOICES_ADD_EDIT:           'INVOICES_ADD/EDIT',
  INVOICES_DELETE:             'INVOICES_DELETE',
  INVOICES_SHOW_MENU:          'INVOICES_SHOW_MENU',
  INVOICES_SEND_INVOICES:      'INVOICES_SEND_INVOICES',
  INVOICES_RECEIVE_PAYMENTS:   'INVOICES_RECEIVE_PAYMENTS',
  // WP-06 (2026-09-22): narrower than INVOICES_RECEIVE_PAYMENTS — refunding a
  // card charge or recording a 'refund'-kind ledger row (write_off stays on
  // INVOICES_RECEIVE_PAYMENTS — it waives a balance, no money moves). Granted
  // by default to Administrator + Accounting only (not Manager — Rick's ruling);
  // see backend/supabase/migrations/20260922200000_invoices_refund_payments_permission.sql.
  INVOICES_REFUND_PAYMENTS:    'INVOICES_REFUND_PAYMENTS',
  // Tony ruling (2026-09-26): "Mark Paid" (recording the remaining balance
  // as paid and flipping the invoice to Paid) is gated separately from
  // INVOICES_RECEIVE_PAYMENTS — a user can be allowed to record a normal
  // payment without being allowed to unilaterally mark an invoice paid, and
  // vice versa. Granted by default to Administrator + Accounting only (same
  // default set as INVOICES_REFUND_PAYMENTS); see
  // backend/supabase/migrations/20260926120000_invoices_mark_paid_permission.sql.
  INVOICES_MARK_PAID:          'INVOICES_MARK_PAID',
  // Wave 1 tax-gaps (2026-10-11): void an invoice / logged revision of an
  // issued invoice / credit memos. Default Administrator + Accounting; see
  // backend/supabase/migrations/20261011120400_money_doc_permissions.sql.
  INVOICES_VOID:               'INVOICES_VOID',
  INVOICES_REVISE:             'INVOICES_REVISE',
  INVOICES_CREDIT_MEMO:        'INVOICES_CREDIT_MEMO',
  // Wave 2 tax-gaps (2026-10-11): write-off controls, accounting period lock, tax exemption certificates.
  // Defaults: see backend/supabase/migrations/20261012130000_w2_permissions.sql.
  INVOICES_WRITE_OFF:            'INVOICES_WRITE_OFF',
  INVOICES_WRITE_OFF_UNLIMITED:  'INVOICES_WRITE_OFF_UNLIMITED',
  ACCOUNTING_PERIOD_LOCK:        'ACCOUNTING_PERIOD_LOCK',
  ACCOUNTING_PERIOD_OVERRIDE:    'ACCOUNTING_PERIOD_OVERRIDE',
  TAX_EXEMPTION_MANAGE:          'TAX_EXEMPTION_MANAGE',
  TAX_EXEMPTION_OVERRIDE:        'TAX_EXEMPTION_OVERRIDE',
  // Wave 3A tax-gaps (2026-10-11): document numbering edits (replaces SETTINGS_COMPANY for that) and accounting
  // method. Defaults: see backend/supabase/migrations/20261013100000_w3_permissions.sql.
  NUMBERING_EDIT:                'NUMBERING_EDIT',
  ACCOUNTING_BASIS_EDIT:         'ACCOUNTING_BASIS_EDIT',
  // Wave 3B tax-gaps (2026-10-11): vendor bills / 1099 and reverse-invoice-to-job. Defaults: see
  // backend/supabase/migrations/20261014100000_w3b_permissions.sql (ADD_EDIT = every group holding PO_ADD/EDIT).
  VENDOR_BILLS_ADD_EDIT:         'VENDOR_BILLS_ADD_EDIT',
  VENDOR_BILLS_APPROVE:          'VENDOR_BILLS_APPROVE',
  VENDOR_BILLS_PAY:              'VENDOR_BILLS_PAY',
  VENDOR_1099_REPORT:            'VENDOR_1099_REPORT',
  VENDOR_1099_EXPORT:            'VENDOR_1099_EXPORT',
  INVOICES_REVERSE:              'INVOICES_REVERSE',
  // Wave 3C-lite tax-gaps (2026-10-11): quote revise / re-issue and payment-terms rules. Defaults: see
  // backend/supabase/migrations/20261015100000_w3c_permissions.sql (QUOTES_REVISE = every group holding QUOTES_ADD/EDIT).
  QUOTES_REVISE:                 'QUOTES_REVISE',
  PAYMENT_TERMS_EDIT:            'PAYMENT_TERMS_EDIT',
  AGREEMENTS_ADD_EDIT:         'AGREEMENTS_ADD/EDIT',
  AGREEMENTS_DELETE:           'AGREEMENTS_DELETE',
  AGREEMENTS_SHOW_MENU:        'AGREEMENTS_SHOW_MENU',
  AGREEMENTS_GENERATE_FILES:   'AGREEMENTS_GENERATE_FILES',
  PO_ADD_EDIT:                 'PO_ADD/EDIT',
  PO_EDIT_DELETE:              'PO_EDIT_DELETE',
  PO_SHOW_MENU:                'PO_SHOW_MENU',
  PO_SEND_TO_VENDOR:           'PO_SEND_TO_VENDOR',
  VENDORS_ADD_EDIT:            'VENDORS_ADD/EDIT',
  VENDORS_DELETE:              'VENDORS_DELETE',
  VENDOR_SHOW_MENU:            'VENDOR_SHOW_MENU',
  EXPENSE_SHOW_MENU:           'EXPENSE_SHOW_MENU',
  TIME_ADD:                    'TIME_ADD',
  TIME_EDIT_DELETE:            'TIME_EDIT/DELETE',
  TIME_VIEW:                   'TIME_VIEW',
  EXPENSES_ADD:                'EXPENSES_ADD',
  EXPENSES_EDIT_DELETE:        'EXPENSES_EDIT/DELETE',
  JOBCOST_VIEW:                'JOBCOST_VIEW',
  PRICING_VIEW:                'PRICING_VIEW',
  COSTS_VIEW:                  'COSTS_VIEW',
  PRICING_ADD_EDIT:            'PRICING_ADD/EDIT',
  SERVICE_ITEM_VIEW:           'SERVICE_ITEM_VIEW',
  SERVICE_ITEM_ADD_EDIT:       'SERVICE_ITEM_ADD/EDIT',
  FLEET_ADD_EDIT:              'FLEET_ADD/EDIT',
  FLEET_DELETE:                'FLEET_DELETE',
  FLEET_SHOW_MENU:             'FLEET_SHOW_MENU',
  SOLUTIONS_ADD_EDIT:          'SOLUTIONS_ADD/EDIT',
  SOLUTIONS_DELETE:            'SOLUTIONS_DELETE',
  SOLUTIONS_SHOW_MENU:         'SOLUTIONS_SHOW_MENU',
  FILES_VIEW_ALL:              'FILES_VIEW_ALL',
  CLIENT_COMMS_VIEW:           'CLIENT_COMMS_VIEW',
  REPORTS_VIEW:                'REPORTS_VIEW',
  EXPORT_DATA:                 'EXPORT_DATA',
  DISPATCH_VIEW:               'DISPATCH_VIEW',
  DISPATCH_MANAGE:             'DISPATCH_MANAGE',
  ADMIN_SHOW_MENU:             'ADMIN_SHOW_MENU',
  TEAM_VIEW:                   'TEAM_VIEW',
  TEAM_EDIT:                   'TEAM_EDIT',
  TEAM_DELETE:                 'TEAM_DELETE',
  TEAM_MANAGE_PERMISSIONS:     'TEAM_MANAGE_PERMISSIONS',
  SEE_OTHER_TEAM_MEMBER:       'SEE_OTHER_TEAM_MEMBER',
  PRINT_OTHER_TEAM_MEMBER:     'PRINT_OTHER_TEAM_MEMBER',
  EDIT_OTHER_TEAM_MEMBER:      'EDIT_OTHER_TEAM_MEMBER',
  SETTINGS_COMPANY:            'SETTINGS_COMPANY',
  SETTINGS_BILLING:            'SETTINGS_BILLING',
  SETTINGS_PRODUCTS_SERVICES:  'SETTINGS_PRODUCTS_SERVICES',
  SETTINGS_CUSTOM_FIELDS:      'SETTINGS_CUSTOM_FIELDS',
  SETTINGS_AUTOMATIONS:        'SETTINGS_AUTOMATIONS',
  // WP-06 Wave 2 (2026-09-22): api-keys.ts had zero authorization anywhere —
  // any authenticated tenant user could mint, revoke, or permanently delete
  // tenant API keys. Migration 20260922210000_api_keys_manage_permission.sql
  // already applied to Dev + all 5 tenant hubs, deliberately assigned to no
  // group by default — only Administrator (via ADMIN_FULL) has it out of the box.
  API_KEYS_MANAGE:              'API_KEYS_MANAGE',
  GALLERY_VIEW:                'GALLERY_VIEW',
  GALLERY_ADD:                 'GALLERY_ADD',
  PHOTO_DELETE:                'PHOTO_DELETE',
  INSIGHTS_VIEW:               'INSIGHTS_VIEW',
  INSIGHTS_FINANCIAL_VIEW:     'INSIGHTS_FINANCIAL_VIEW',
  INSIGHTS_COMMUNICATIONS_VIEW: 'INSIGHTS_COMMUNICATIONS_VIEW',
  INSIGHTS_TEAM_PERFORMANCE_VIEW: 'INSIGHTS_TEAM_PERFORMANCE_VIEW',
  INSIGHTS_RECORD_SEARCH:      'INSIGHTS_RECORD_SEARCH',
  // Bulk-delete permissions (list-view multi-select delete — separate from single-record delete)
  CLIENTS_BULK_DELETE:         'CLIENTS_BULK_DELETE',
  LEADS_BULK_DELETE:           'LEADS_BULK_DELETE',
  REQUESTS_BULK_DELETE:        'REQUESTS_BULK_DELETE',
  QUOTES_BULK_DELETE:          'QUOTES_BULK_DELETE',
  JOBS_BULK_DELETE:            'JOBS_BULK_DELETE',
  INVOICES_BULK_DELETE:        'INVOICES_BULK_DELETE',
  AGREEMENTS_BULK_DELETE:      'AGREEMENTS_BULK_DELETE',
  PO_BULK_DELETE:              'PO_BULK_DELETE',
  FLEET_BULK_DELETE:           'FLEET_BULK_DELETE',
  SOLUTIONS_BULK_DELETE:       'SOLUTIONS_BULK_DELETE',
  VENDORS_BULK_DELETE:         'VENDORS_BULK_DELETE',
  // Change-status permissions (change an entity's status from its detail page,
  // without full add/edit rights). Requests + Jobs default ON for all groups;
  // Quotes default OFF (point exists but ungranted — enable per-group in matrix).
  REQUESTS_CHANGE_STATUS:      'REQUESTS_CHANGE_STATUS',
  QUOTES_CHANGE_STATUS:        'QUOTES_CHANGE_STATUS',
  JOBS_CHANGE_STATUS:          'JOBS_CHANGE_STATUS',
  // Bundles + Quote Templates (QuoteWorks upgrade P4, Rick Feedback II)
  QUOTES_MANAGE_BUNDLES:       'QUOTES_MANAGE_BUNDLES',
  QUOTES_MANAGE_TEMPLATES:     'QUOTES_MANAGE_TEMPLATES',
  // Accounting Link (WS-H1) — access to the linked accounting system (QuickBooks
  // Online / Xero). Independent of JobMaster's own invoice/insights permissions.
  // Deny-by-default: new/custom access groups start with all ten OFF.
  ACCT_LINK_VIEW_DASHBOARD:     'ACCT_LINK_VIEW_DASHBOARD',
  ACCT_LINK_VIEW_CLIENT_INVOICES: 'ACCT_LINK_VIEW_CLIENT_INVOICES',
  ACCT_LINK_VIEW_CLIENT_PAYMENTS: 'ACCT_LINK_VIEW_CLIENT_PAYMENTS',
  ACCT_LINK_VIEW_CLIENT_BALANCE:  'ACCT_LINK_VIEW_CLIENT_BALANCE',
  ACCT_LINK_VIEW_REPORTS:       'ACCT_LINK_VIEW_REPORTS',
  ACCT_LINK_VIEW_SYNC_HISTORY:  'ACCT_LINK_VIEW_SYNC_HISTORY',
  ACCT_LINK_EXPORT:             'ACCT_LINK_EXPORT',
  ACCT_LINK_RUN_SYNC:           'ACCT_LINK_RUN_SYNC',
  ACCT_LINK_MANAGE_SETTINGS:    'ACCT_LINK_MANAGE_SETTINGS',
  ACCT_LINK_MANAGE_CONNECTION:  'ACCT_LINK_MANAGE_CONNECTION',
} as const;

// The ten Accounting Link permission points — used server-side by the
// acct-link-guard "any of the ten" checks (status routes, sync-history)
// and client-side to detect "has some accounting rights but not #10".
export const ACCT_LINK_PERMISSIONS: Permission[] = [
  'ACCT_LINK_VIEW_DASHBOARD',
  'ACCT_LINK_VIEW_CLIENT_INVOICES',
  'ACCT_LINK_VIEW_CLIENT_PAYMENTS',
  'ACCT_LINK_VIEW_CLIENT_BALANCE',
  'ACCT_LINK_VIEW_REPORTS',
  'ACCT_LINK_VIEW_SYNC_HISTORY',
  'ACCT_LINK_EXPORT',
  'ACCT_LINK_RUN_SYNC',
  'ACCT_LINK_MANAGE_SETTINGS',
  'ACCT_LINK_MANAGE_CONNECTION',
];

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// ADMIN_FULL overrides every other check — one person with it passes everything.
export function can(permissions: string[], permission: Permission): boolean {
  return permissions.includes(PERMISSIONS.ADMIN_FULL) || permissions.includes(permission);
}

// Convenience: check multiple — all must pass.
export function canAll(permissions: string[], ...required: Permission[]): boolean {
  return required.every(p => can(permissions, p));
}

// Convenience: check multiple — any one passing is enough.
export function canAny(permissions: string[], ...required: Permission[]): boolean {
  return required.some(p => can(permissions, p));
}
